import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createStore } from 'zustand/vanilla';
import { devtools } from 'zustand/middleware';
import { useStore } from 'zustand';

export type RemoteKey = readonly unknown[];
export type Snapshot<T> = {
  data?: T;
  error: Error | null;
  status: 'pending' | 'success' | 'error';
  fetchStatus: 'idle' | 'fetching';
  updatedAt: number;
  invalidated: boolean;
};
type RemoteState = { queries: Record<string, Snapshot<unknown>>; generation:number };
export type QueryOptions<T> = {
  queryKey: RemoteKey;
  queryFn: (context: { signal: AbortSignal; isCurrent: () => boolean }) => Promise<T>;
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number | false;
  terminal?: (data: T) => boolean;
  refetchOnWindowFocus?: boolean;
};
type Observer = { options: QueryOptions<unknown> };
const EMPTY: Snapshot<never> = { error: null, status: 'pending', fetchStatus: 'idle', updatedAt: 0, invalidated: false };

function stable(value: unknown): string {
  return JSON.stringify(value, (_, item) => item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b))) : item);
}
const retryable = (error: unknown) => {
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  const status = error && typeof error === 'object' && 'status' in error
    ? Number((error as { status?: number }).status) : 0;
  return ![401, 403, 404, 409].includes(status) && (status === 0 || status === 429 || status >= 500);
};
const pause = (ms: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  if (signal.aborted) { reject(signal.reason); return; }
  const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, ms);
  const abort = () => { clearTimeout(timer); reject(signal.reason); };
  signal.addEventListener('abort', abort, { once: true });
});

export class RemoteDataClient {
  readonly store = createStore<RemoteState>()(devtools(
    () => ({ queries: {}, generation:0 }),
    { name: 'media-remote-data', enabled: import.meta.env.DEV },
  ));
  private readonly observers = new Map<string, Set<Observer>>();
  private readonly inflight = new Map<string, { controller: AbortController; promise: Promise<unknown>;
    outdated: boolean; refetchAfter: boolean }>();
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly releaseTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private generation = 0;
  private readonly accessed = new Map<string,number>();
  private listening = false;
  private readonly resume = () => {
    if(typeof document!=='undefined' && document.hidden) return;
    if(typeof navigator!=='undefined' && navigator.onLine===false) return;
    for(const observers of this.observers.values()) {
      const active=[...observers].find(item=>item.options.enabled!==false && item.options.refetchOnWindowFocus!==false);
      if(active) void this.load(active.options);
    }
  };
  private listen(enabled:boolean) {
    if(typeof window==='undefined' || typeof document==='undefined' || this.listening===enabled)return;
    this.listening=enabled;
    const method=enabled?'addEventListener':'removeEventListener';
    window[method]('focus',this.resume);window[method]('online',this.resume);
    document[method]('visibilitychange',this.resume);
  }
  private collect() {
    const inactive=[...this.accessed].filter(([key])=>!this.observers.has(key) && !this.inflight.has(key)).sort((a,b)=>a[1]-b[1]);
    const queries={...this.store.getState().queries};let changed=false;
    let size=Object.keys(queries).length;
    for(const [key,at] of inactive) if(size>128 || Date.now()-at>=5*60_000) {
      if(key in queries){delete queries[key];size--;changed=true;}this.accessed.delete(key);
    }
    if(changed)this.store.setState({queries},false,'query/collect');
  }

  key(queryKey: RemoteKey): string { return stable(queryKey); }
  get<T>(queryKey: RemoteKey): Snapshot<T> {
    const key=this.key(queryKey);if(key in this.store.getState().queries)this.accessed.set(key,Date.now());
    return (this.store.getState().queries[key] ?? EMPTY) as Snapshot<T>;
  }
  private update(key: string, patch: Partial<Snapshot<unknown>>) {
    this.accessed.set(key,Date.now());
    const current = this.store.getState().queries[key] ?? EMPTY;
    this.store.setState({ queries: { ...this.store.getState().queries, [key]: { ...current, ...patch } } }, false, `query/${key}`);
  }

  observe<T>(options: QueryOptions<T>): () => void {
    this.collect();this.listen(true);
    const key = this.key(options.queryKey);
    const observer = { options: options as QueryOptions<unknown> };
    const set = this.observers.get(key) ?? new Set<Observer>();
    set.add(observer);
    this.observers.set(key, set);
    const pendingRelease = this.releaseTimers.get(key);
    if (pendingRelease) clearTimeout(pendingRelease);
    this.releaseTimers.delete(key);
    if (options.enabled !== false) void this.load(options);
    this.schedule(key);
    return () => {
      set.delete(observer);
      if (set.size) { this.schedule(key); return; }
      this.observers.delete(key);
      this.accessed.set(key,Date.now());
      if(!this.observers.size)this.listen(false);
      this.stopTimer(key);
      // React StrictMode remounts immediately; cancel only after that chance.
      this.releaseTimers.set(key, setTimeout(() => {
        if (!this.observers.has(key)) this.inflight.get(key)?.controller.abort();
        this.releaseTimers.delete(key);
        this.collect();
      }, 0));
    };
  }

  async load<T>(options: QueryOptions<T>, force = false, throwError = false): Promise<T | undefined> {
    if (options.enabled === false) return this.get<T>(options.queryKey).data;
    const key = this.key(options.queryKey);
    const current = this.get<T>(options.queryKey);
    const fresh = current.updatedAt && !current.invalidated &&
      (options.staleTime === Infinity || Date.now() - current.updatedAt < (options.staleTime ?? 0));
    if (!force && fresh) return current.data;
    const pending = this.inflight.get(key);
    if (pending) {
      const value = await pending.promise as T | undefined;
      if (throwError && value === undefined) {
        const error = this.get<T>(options.queryKey).error;
        if (error) throw error;
      }
      return value;
    }
    const controller = new AbortController();
    const generation = this.generation;
    const request = { controller, promise: undefined as unknown as Promise<unknown>, outdated: false, refetchAfter: false };
    const isCurrent = () => !controller.signal.aborted && !request.outdated && generation === this.generation;
    this.update(key, { fetchStatus: 'fetching', error: current.data === undefined ? null : current.error });
    const promise = (async () => {
      try {
        for (let attempt = 0; ; attempt += 1) {
          try {
            const data = await options.queryFn({ signal: controller.signal, isCurrent });
            if (!isCurrent()) return undefined;
            this.update(key, { data, error: null, status: 'success', fetchStatus: 'idle',
              updatedAt: Date.now(), invalidated: false });
            return data;
          } catch (error) {
            if (!isCurrent()) return undefined;
            if (attempt >= 2 || !retryable(error)) throw error;
            await pause(Math.min(2_000, 250 * 2 ** attempt) + Math.random() * 100, controller.signal);
          }
        }
      } catch (error) {
        if (isCurrent()) {
          const denied=[401,403].includes(Number((error as {status?:number})?.status));
          this.update(key, { error: error instanceof Error ? error : new Error(String(error)),
            ...(denied?{data:undefined}:{}),status: denied || current.data === undefined ? 'error' : 'success', fetchStatus: 'idle' });
        }
        if (throwError && isCurrent()) throw error;
        return undefined;
      } finally {
        if (this.inflight.get(key) === request) {
          this.inflight.delete(key);
          if (request.outdated) this.update(key, { fetchStatus: 'idle' });
          if (request.outdated && request.refetchAfter && !controller.signal.aborted && generation === this.generation) {
            const observer = [...(this.observers.get(key) ?? [])].find(item => item.options.enabled !== false);
            if (observer) void this.load(observer.options, true);
          }
        }
        this.schedule(key);
      }
    })();
    request.promise = promise;
    this.inflight.set(key, request);
    return promise;
  }

  invalidate(prefix: RemoteKey, refetch = true, exact = false) {
    const prefixHash = stable(prefix);
    for (const key of Object.keys(this.store.getState().queries)) {
      const parts = JSON.parse(key) as unknown[];
      if (exact && parts.length !== prefix.length) continue;
      if (stable(parts.slice(0, prefix.length)) !== prefixHash) continue;
      const observer = [...(this.observers.get(key) ?? [])].find((item) => item.options.enabled !== false);
      const pending = this.inflight.get(key);
      if (pending && refetch) {
        // A committed approval/change supersedes this read. Let its transport
        // settle, then coalesce completion events into one current read.
        // Progressive publishers use isCurrent() to fence partial responses.
        pending.outdated = true;
        pending.refetchAfter ||= Boolean(observer);
      }
      this.update(key, { invalidated: true });
      if (observer && refetch && !pending) void this.load(observer.options, true);
    }
  }

  set<T>(queryKey: RemoteKey, value: T | ((previous: T | undefined) => T | undefined), updatedAt = Date.now()) {
    const previous = this.get<T>(queryKey).data;
    const data = typeof value === 'function' ? (value as (previous: T | undefined) => T | undefined)(previous) : value;
    if (data === undefined) return;
    this.update(this.key(queryKey), { data, error: null, status: 'success', updatedAt, invalidated: false });
    this.collect();
  }

  entries() {
    return Object.entries(this.store.getState().queries).map(([key, snapshot]) => ({
      queryKey: JSON.parse(key) as RemoteKey, snapshot,
    }));
  }

  cancel(prefix: RemoteKey, exact = false) {
    for (const [key, pending] of this.inflight) {
      const parts = JSON.parse(key) as unknown[];
      if (exact ? stable(parts) === stable(prefix) : stable(parts.slice(0, prefix.length)) === stable(prefix)) {
        pending.controller.abort();
        this.inflight.delete(key);
        this.update(key, { fetchStatus: 'idle' });
      }
    }
  }

  remove(prefix: RemoteKey, exact = false) {
    this.cancel(prefix, exact);
    const next = { ...this.store.getState().queries };
    for (const key of Object.keys(next)) {
      const parts = JSON.parse(key) as unknown[];
      if (exact ? stable(parts) === stable(prefix) : stable(parts.slice(0, prefix.length)) === stable(prefix))
        delete next[key];
    }
    this.store.setState({ queries: next }, false, 'query/remove');
  }

  activeOptions(prefix: RemoteKey, exact = false) {
    return [...this.observers.entries()].filter(([key]) => {
      const parts = JSON.parse(key) as unknown[];
      return exact ? stable(parts) === stable(prefix) : stable(parts.slice(0, prefix.length)) === stable(prefix);
    }).flatMap(([, observers]) => [...observers].filter(item => item.options.enabled !== false).map(item => item.options));
  }

  clear() {
    this.generation += 1;
    for (const pending of this.inflight.values()) pending.controller.abort();
    this.inflight.clear();
    for (const key of this.timers.keys()) this.stopTimer(key);
    for (const timer of this.releaseTimers.values()) clearTimeout(timer);
    this.releaseTimers.clear();
    this.accessed.clear();
    // Old observers must not start reads while the next identity is being
    // committed. React re-subscribes after the new principal has rendered.
    this.store.setState({ queries: {}, generation:this.generation }, false, 'session/clear');
  }

  private stopTimer(key: string) {
    const timer = this.timers.get(key);
    if (timer) clearTimeout(timer);
    this.timers.delete(key);
  }

  private schedule(key: string) {
    this.stopTimer(key);
    const observer = [...(this.observers.get(key) ?? [])].find((item) => item.options.enabled !== false &&
      typeof item.options.refetchInterval === 'number' && item.options.refetchInterval > 0);
    if (!observer) return;
    const interval = observer.options.refetchInterval as number;
    const snapshot = this.store.getState().queries[key];
    if (snapshot?.data !== undefined && observer.options.terminal?.(snapshot.data)) return;
    const delay = snapshot?.error ? Math.min(interval * 8, interval * 2) : interval;
    this.timers.set(key, setTimeout(() => {
      if (typeof document !== 'undefined' && document.hidden) { this.schedule(key); return; }
      void this.load(observer.options, true);
    }, delay));
  }
}

export const dataClient = new RemoteDataClient();

export function useRemoteQuery<T>(options: QueryOptions<T>, client = dataClient) {
  const generation=useStore(client.store,state=>state.generation);
  const serializedKey = stable(options.queryKey);
  const key = useMemo(() => serializedKey, [serializedKey]);
  const current = useRef(options);
  current.current = options;
  const snapshot = useStore(client.store, useCallback(
    (state) => (state.queries[key] ?? EMPTY) as Snapshot<T>, [key],
  ));
  useEffect(() => client.observe({ ...current.current, queryKey: JSON.parse(key) as RemoteKey,
    queryFn: context => current.current.queryFn(context) }), [client, key, options.enabled,generation]);
  return {
    data: snapshot.data,
    error: snapshot.error,
    status: snapshot.status,
    isLoading: options.enabled !== false && snapshot.data === undefined && snapshot.status === 'pending',
    isFetching: snapshot.fetchStatus === 'fetching',
    isSuccess: snapshot.status === 'success',
    refetch: () => client.load(current.current, true),
  };
}
